import React, { useEffect, useState } from 'react';

import { ChevronDown } from 'lucide-react';

import {
	useGetDataRetentionQuery,
	useUpdateDataRetentionMutation,
} from '../../api/dataRetention';

import type {
	DataRetentionSetting,
	RetentionMode,
	RetentionPeriod,
} from '../../api/dataRetention';

const KEEP_ALL = 'keep_all';

const PERIOD_OPTIONS: Array<{
	value: RetentionPeriod | typeof KEEP_ALL;
	label: string;
}> = [
	{ value: KEEP_ALL, label: 'Keep everything' },
	{ value: '1_month', label: '1 month' },
	{ value: '1_day', label: '1 day' },
	{ value: '1_week', label: '1 week' },
	{ value: '2_months', label: '2 months' },
	{ value: '3_months', label: '3 months' },
	{ value: '12_months', label: '12 months' },
	{ value: '24_months', label: '24 months' },
];

const MODE_OPTIONS: Array<{ value: RetentionMode; label: string }> = [
	{
		value: 'keep_audit_record',
		label: 'Delete statements, keep the audit record',
	},
	{ value: 'delete_everything', label: 'Delete everything' },
];

export const FIELD_LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-2';
export const FIELD_INPUT_CLASS =
	'block w-full border border-gray-300 rounded-lg bg-white px-4 py-2 text-sm leading-5 text-gray-900 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50';

/** Tenant data retention (ROC-107). Not available where the API has no tenant to store it on. */
export const useDataRetention = (isAdmin: boolean) => {
	const { data, isError } = useGetDataRetentionQuery(undefined, {
		skip: !isAdmin,
	});
	const [updateRetention, { isLoading: isSaving }] =
		useUpdateDataRetentionMutation();
	const [draft, setDraft] = useState<DataRetentionSetting>({
		period: null,
		mode: null,
	});

	useEffect(() => {
		if (data) setDraft(data);
	}, [data]);

	return {
		available: !!data && !isError,
		draft,
		setDraft,
		hasChanges:
			!!data && (draft.period !== data.period || draft.mode !== data.mode),
		isSaving,
		save: () => updateRetention(draft).unwrap(),
	};
};

const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (
	props,
) => (
	<div className="relative">
		<select
			{...props}
			className={`${FIELD_INPUT_CLASS} appearance-none cursor-pointer pr-10 text-ellipsis`}
		/>
		<ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
	</div>
);

interface DataRetentionFieldsProps {
	draft: DataRetentionSetting;
	onChange: (draft: DataRetentionSetting) => void;
	disabled?: boolean;
}

export const DataRetentionFields: React.FC<DataRetentionFieldsProps> = ({
	draft,
	onChange,
	disabled = false,
}) => (
	<div>
		<div className="text-base font-medium text-gray-900 mb-4">
			Data retention
		</div>
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div>
				<label htmlFor="retention-period" className={FIELD_LABEL_CLASS}>
					Keep statements and analyses for
				</label>
				<Select
					id="retention-period"
					value={draft.period ?? KEEP_ALL}
					disabled={disabled}
					onChange={(e) =>
						onChange(
							e.target.value === KEEP_ALL
								? { period: null, mode: null }
								: {
										period: e.target.value as RetentionPeriod,
										mode: draft.mode ?? 'keep_audit_record',
									},
						)
					}
				>
					{PERIOD_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</Select>
			</div>
			<div>
				<label htmlFor="retention-mode" className={FIELD_LABEL_CLASS}>
					After that
				</label>
				<Select
					id="retention-mode"
					value={draft.mode ?? 'keep_audit_record'}
					disabled={disabled || !draft.period}
					onChange={(e) =>
						onChange({ ...draft, mode: e.target.value as RetentionMode })
					}
				>
					{MODE_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</Select>
			</div>
		</div>
	</div>
);
